import { Link } from "react-router-dom";
import {
  ChevronDown,
  LogOut,
  LayoutDashboard,
  User,
  Car,
  Bell,
  Heart,
  Shield,
  Users,
  MapPin,
  Clock,
  Menu,
  X,
  Settings,
  HelpCircle,
  BookOpen,
  Truck,
  Phone,
  Mail,
  Home,
  AlertTriangle,
  Gift,
  Building,
  Calendar
} from "lucide-react";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const DesktopNavbar = ({
  isActivePath,
  isVolunteerDropdownOpen,
  setIsVolunteerDropdownOpen,
  volunteerLinks,
  vehicleLinks,
  handleRegisterClick,
  user,
  handleLogoutClick,
  handleDashboardClick
}) => {
  const [isVehicleDropdownOpen, setIsVehicleDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // Mock notifications - replace with real data
  useEffect(() => {
    setNotifications([
      { id: 1, title: "Emergency Blood Request", message: "New urgent request in your area", time: "2 min ago", type: "emergency" },
      { id: 2, title: "Donation Reminder", message: "You're eligible to donate again", time: "1 hour ago", type: "reminder" },
      { id: 3, title: "Campaign Update", message: "New NGO campaign launched", time: "3 hours ago", type: "campaign" }
    ]);
  }, []);

  const unreadNotifications = notifications.filter(n => n.type === "emergency").length;

  return (
    <div className="hidden lg:flex items-center space-x-4">
      {/* Primary Navigation Links */}
      <div className="flex items-center space-x-2">
        <NavLink
          to="/"
          isActive={isActivePath('/')}
          icon={<Home className="w-4 h-4" />}
        >
          Home
        </NavLink>

        <NavLink
          to="/emergency-blood"
          isActive={isActivePath('/emergency-blood')}
          icon={<AlertTriangle className="w-4 h-4" />}
          badge="URGENT"
          badgeColor="red"
        >
          Emergency Blood
        </NavLink>

        <NavLink
          to="/projects"
          isActive={isActivePath('/projects')}
          icon={<Gift className="w-4 h-4" />}
        >
          Donate Campaigns
        </NavLink>
      </div>

      {/* Divider */}
      <div className="w-px h-8 bg-gray-300"></div>

      {/* Service Dropdowns */}
      <div className="flex items-center space-x-2">
        {/* Volunteer Services Dropdown */}
        <ModernDropdown
          isOpen={isVolunteerDropdownOpen}
          setIsOpen={setIsVolunteerDropdownOpen}
          isActive={isActivePath('/volunteer') || isActivePath('/ngo-registration') ||
            isActivePath('/vehicle-registration') || isActivePath('/register-hospital')}
          icon={<Users className="w-4 h-4" />}
          title="Volunteer Services"
        >
          <div className="grid grid-cols-1 gap-1 p-3">
            <div className="mb-3 px-2">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Join Our Community</h3>
              <p className="text-xs text-gray-600">Help save lives by volunteering your time and resources</p>
            </div>
            {volunteerLinks.map((link) => (
              <ModernDropdownLink
                key={link.name}
                to={link.path}
                onClick={link.onClick}
                isActive={isActivePath(link.path)}
                icon={link.name.includes("NGO") && link.name.includes("Login") ? <User className="w-4 h-4" /> :
                  link.name.includes("NGO") ? <Building className="w-4 h-4" /> :
                    link.name.includes("Hospital") && link.name.includes("Login") ? <User className="w-4 h-4" /> :
                      link.name.includes("Hospital") ? <Shield className="w-4 h-4" /> :
                        <Users className="w-4 h-4" />}
                title={link.name}
                description={link.name.includes("NGO") && link.name.includes("Login") ? "Access your NGO dashboard" :
                  link.name.includes("NGO") ? "Register your NGO" :
                    link.name.includes("Hospital") && link.name.includes("Login") ? "Access your hospital dashboard" :
                      link.name.includes("Hospital") ? "Register your hospital" :
                        "Join as volunteer"}
              />
            ))}
          </div>
        </ModernDropdown>

        {/* Vehicle Services Dropdown */}
        <ModernDropdown
          isOpen={isVehicleDropdownOpen}
          setIsOpen={setIsVehicleDropdownOpen}
          isActive={isActivePath('/vehicle/login') || isActivePath('/vehicle/register') ||
            isActivePath('/vehicle/dashboard')}
          icon={<Truck className="w-4 h-4" />}
          title="Vehicle Services"
        >
          <div className="grid grid-cols-1 gap-1 p-3">
            <div className="mb-3 px-2">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Emergency Transportation</h3>
              <p className="text-xs text-gray-600">Fast and reliable blood transport services</p>
            </div>

            {/* Emergency Services */}
            <div className="mb-3">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
                Emergency Services
              </div>
              <div className="space-y-1">
                {vehicleLinks.filter(link => !link.group).map((link) => (
                  <ModernDropdownLink
                    key={link.name}
                    to={link.path}
                    onClick={link.onClick}
                    isActive={isActivePath(link.path)}
                    icon={link.icon === 'car' ? <Car className="w-4 h-4" /> :
                      link.icon === 'search' ? <Clock className="w-4 h-4" /> :
                        <Clock className="w-4 h-4" />}
                    title={link.name}
                    description={link.description}
                  />
                ))}
              </div>
            </div>

            {/* For Drivers */}
            <div className="border-t border-gray-100 pt-3">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
                For Drivers
              </div>
              <div className="space-y-1">
                {vehicleLinks.filter(link => link.group === 'owner').map((link) => (
                  <ModernDropdownLink
                    key={link.name}
                    to={link.path}
                    onClick={link.onClick}
                    isActive={isActivePath(link.path)}
                    icon={link.icon === 'login' ? <User className="w-4 h-4" /> :
                      link.icon === 'user-plus' ? <Users className="w-4 h-4" /> :
                        <LayoutDashboard className="w-4 h-4" />}
                    title={link.name}
                    description={link.description}
                  />
                ))}
              </div>
            </div>
          </div>
        </ModernDropdown>
      </div>
    </div>
  );
};

// New Modern Components
const NavLink = ({ to, children, isActive, icon, badge, badgeColor = "red" }) => (
  <Link
    to={to}
    className={`group relative px-3 py-1.5 rounded-xl text-sm font-medium transition-all duration-200 ${isActive
      ? 'text-red-600 bg-red-50 border border-red-200'
      : 'text-gray-700 hover:text-red-600 hover:bg-red-50 hover:border hover:border-red-200'
      }`}
  >
    <div className="flex items-center space-x-2">
      {icon}
      <span>{children}</span>
      {badge && (
        <span className={`px-2 py-0.5 text-xs rounded-full bg-${badgeColor}-100 text-${badgeColor}-600 font-semibold`}>
          {badge}
        </span>
      )}
    </div>
  </Link>
);

const ModernDropdown = ({ children, isOpen, setIsOpen, isActive, icon, title }) => (
  <div className="relative">
    <button
      className={`group px-3 py-1.5 rounded-xl text-sm font-medium transition-all duration-200 inline-flex items-center space-x-2 ${isActive
        ? 'text-red-600 bg-red-50 border border-red-200'
        : 'text-gray-700 hover:text-red-600 hover:bg-red-50 hover:border hover:border-red-200'
        }`}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {icon}
      <span>{title}</span>
      <motion.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.2 }}
      >
        <ChevronDown className="w-4 h-4" />
      </motion.div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="absolute left-0 mt-2 w-64 rounded-2xl shadow-2xl bg-white border border-gray-200 z-50 overflow-hidden"
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const ModernDropdownLink = ({ to, children, onClick, isActive, icon, title, description }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`block p-2 rounded-xl mx-1 transition-all duration-200 ${isActive
      ? 'bg-red-50 text-red-600 border border-red-200'
      : 'text-gray-700 hover:bg-gray-50 hover:text-red-600'
      }`}
  >
    <div className="flex items-start space-x-2.5">
      <div className={`p-1.5 rounded-lg ${isActive ? 'bg-red-100' : 'bg-gray-100'
        }`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{title}</p>
        {description && (
          <p className="text-[11px] text-gray-500 mt-0.5">{description}</p>
        )}
      </div>
    </div>
  </Link>
);

export default DesktopNavbar;